import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { Box, Container, Tab, Tabs } from '@mui/material';
import { Alert } from '@mui/material';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import Page from '../Page';
import Header from './Header';
import TrainingPlanDetailsEditor from './TrainingPlanDetailsEditor';
import TrainingPlanScheduleEditor from './TrainingPlanScheduleEditor';
import TrainingPlanCollectionEditor from './TrainingPlanCollectionEditor';
import TrainingPlanExtraFiles from './TrainingPlanExtraFiles';
import TrainingPlanPreview from './TrainingPlanPreview';
import AssignTrainingPlanDialog from './AssignTrainingPlanDialog';
import TrainingPlanAnalytics from './TrainingPlanAnalytics';
import ServerError from '../error/ServerError';
import Loading from '../../components/shared/Loading';
import config from '../../config';

const tabs = [
  { value: 'overview', label: 'Overview' },
  { value: 'workouts', label: 'Workouts' },
  { value: 'enrolled', label: 'Enrolled Users' },
  { value: 'files', label: 'Included Files' },
  //  { value: 'weeks', label: 'Week Descriptions' },
];

const TRAINING_PLAN_FRAGMENT = gql`
  fragment TrainingPlanFragment on TrainingPlan {
    __typename
    _id
    title
    desc
    tags
    userId
    files {
      _id
      url
      imageUrl
      label
      sizeInBytes
    }
    coverImages {
      __typename
      aspect
      url
      w
      h
    }
    hasSchedule
    level
    status
    events {
      __typename
      _id
      objectId
      eventType
      title
      desc
      day
      weekNumber
      weekday
      coverImageUrl
    }
    eventCount
    weeks {
      __typename
      weekNumber
      title
      desc
      events {
        __typename
        _id
        objectId
        eventType
        title
        desc
        day
        weekNumber
        weekday
        coverImageUrl
      }
    }
    mobilePreviewUrl
    webUrl
  }
`;
const GET_TRAINING_PLAN_QUERY = gql`
  query GetTrainingPlanQuery($id: ID!) {
    trainingPlan(id: $id) {
      ...TrainingPlanFragment
    }
  }
  ${TRAINING_PLAN_FRAGMENT}
`;

const SAVE_TRAINING_PLAN_MUTATION = gql`
  mutation UpdateTrainingPlan($id: ID!, $input: TrainingPlanInput!) {
    updateTrainingPlan(id: $id, input: $input) {
      ...TrainingPlanFragment
    }
  }
  ${TRAINING_PLAN_FRAGMENT}
`;

const toNullableInt = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
};

const TrainingPlanEditor = () => {
  const params = useParams();
  const [previewRefresh, setPreviewRefresh] = useState(1);
  const handleSaveCompleted = (data) => {
    setTrainingPlan(data.updateTrainingPlan);
  };

  const {
    loading: dataLoading,
    error,
    data,
  } = useQuery(GET_TRAINING_PLAN_QUERY, {
    variables: { id: params.id },
  });
  const [saveTrainingPlan, { error: saveError }] = useMutation(
    SAVE_TRAINING_PLAN_MUTATION,
    {
      onCompleted: handleSaveCompleted,
      refetchQueries: [
        { query: GET_TRAINING_PLAN_QUERY, variables: { id: params.id } },
      ],
    }
  );

  const [trainingPlan, setTrainingPlan] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [currentTab, setCurrentTab] = useState('overview');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);

  const handleTabsChange = (event, value) => {
    setCurrentTab(value);
  };

  const handleSave = async (trainingPlan) => {
    enqueueSnackbar('Saving...', { autoHideDuration: 1000 });

    console.debug(trainingPlan);
    const changes = {
      title: trainingPlan.title,
      desc: trainingPlan.desc,
      tags: trainingPlan.tags,
      coverImages: trainingPlan.coverImages,
      hasSchedule: trainingPlan.hasSchedule,
      level: parseInt(trainingPlan.level),
      status: parseInt(trainingPlan.status),
      files: (trainingPlan.files ?? []).map((f) => ({
        url: f.url,
        imageUrl: f.imageUrl,
        label: f.label,
        sizeInBytes: f.sizeInBytes,
      })),
      weeks: (trainingPlan.weeks ?? [])
        .map((w) => ({
          weekNumber: toNullableInt(w.weekNumber),
          title: w.title,
          desc: w.desc,
        }))
        .filter((w) => w.weekNumber != null),
      events: (trainingPlan.events ?? []).map((e) => ({
        _id: e._id,
        objectId: e.objectId,
        eventType: e.eventType || 0,
        day: toNullableInt(e.day),
      })).filter((e) => e.day != null),
    };

    try {
      await saveTrainingPlan({
        variables: { id: trainingPlan._id, input: changes },
      });

      setTimeout(() => {
        setPreviewRefresh(previewRefresh + 1, 2000);
      });
    } catch (e) {
      console.error(e);
      alert(e.message);
    }
  };

  useEffect(() => {
    if (data?.trainingPlan) {
      setTrainingPlan(data.trainingPlan);
    }
  }, [data]);

  if (dataLoading) return <Loading />;
  if (error || saveError) {
    return (
      <ServerError message={(error || saveError)?.message ?? 'Failed to load'} />
    );
  }
  if (!data) return <ServerError message="No Data" />;

  if (!trainingPlan) {
    return <Alert severity="error">No training plan</Alert>;
  }

  return (
    <Page title={`Training Plan Editor : ${config.appDisplayName}`}>
      <Container maxWidth={false}>
        <Header
          trainingPlan={trainingPlan}
          onAssign={() => setAssignDialogOpen(true)}
          onPreview={() => setCurrentTab('preview')}
        />
        <Box mt={3} sx={{ borderBottom: '1px solid #dbe3ee' }}>
          <Tabs
            onChange={handleTabsChange}
            scrollButtons="auto"
            value={currentTab}
            variant="scrollable"
            textColor="secondary"
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        </Box>
        <Box mt={2}>
          {currentTab === 'overview' && (
            <TrainingPlanDetailsEditor
              key="overview"
              trainingPlan={trainingPlan}
              onSave={handleSave}
              onEditWorkouts={() => setCurrentTab('workouts')}
            />
          )}
          {currentTab === 'workouts' && trainingPlan.hasSchedule && (
            <TrainingPlanScheduleEditor
              key="workouts"
              trainingPlan={trainingPlan}
              onSave={handleSave}
            />
          )}
          {currentTab === 'workouts' && !trainingPlan.hasSchedule && (
            <TrainingPlanCollectionEditor
              key="workouts"
              trainingPlan={trainingPlan}
              onSave={handleSave}
            />
          )}
          {currentTab === 'files' && (
            <TrainingPlanExtraFiles
              key="files"
              trainingPlan={trainingPlan}
              onSave={handleSave}
            />
          )}
          {currentTab === 'enrolled' && (
            <TrainingPlanAnalytics key="enrolled" trainingPlan={trainingPlan} />
          )}
          {currentTab === 'preview' && (
            <TrainingPlanPreview key="preview" trainingPlan={trainingPlan} />
          )}
        </Box>
        <AssignTrainingPlanDialog
          open={assignDialogOpen}
          trainingPlan={trainingPlan}
          onClose={() => setAssignDialogOpen(false)}
        />
      </Container>
    </Page>
  );
};

export default TrainingPlanEditor;
